// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useState, useRef, useEffect } from "react"
import { isAxiosError } from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { API_URL, EMAIL_SIGNUP_ENABLED } from "@/config"
import { FaGithub } from "react-icons/fa"
import {
  Eye,
  EyeOff,
  ChevronLeft,
  Mail,
  CircleAlert,
  Check,
  ChevronDown,
  Lock,
  ArrowDown,
} from "lucide-react"
import { ToSContent } from "@/views/login/component.tosContent"
import { apiClient } from "@/queries/client"
import {
  DEFAULT_LANDING,
  rememberReturnTo,
  safeReturnTo,
} from "@/lib/return-to"
import ebioseMark from "@assets/brand/ebiose_mark_mint.svg"
import { cn } from "@sglara/cn"

const ring = "0 0 0 3px rgba(69,240,200,0.14), 0 0 16px rgba(69,240,200,0.1)"

// Where a freshly signed-in visitor goes: the deep link the guard refused,
// else the cycle list. The session itself lives in the HttpOnly cookie the
// login response sets, so signing in only means dropping the cache the
// anonymous session filled and routing on.
const useSignInLanding = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const destination =
    safeReturnTo((location.state as { returnTo?: unknown } | null)?.returnTo) ??
    DEFAULT_LANDING

  return {
    land: () => {
      queryClient.clear()
      navigate(destination, { replace: true })
    },
    leaveForGithub: (url: string) => {
      rememberReturnTo(destination)
      window.location.href = url
    },
  }
}

const PASSWORD_CRITERIA = [
  { label: "8 characters", test: (s: string) => s.length >= 8 },
  { label: "1 uppercase", test: (s: string) => /[A-Z]/.test(s) },
  { label: "1 number", test: (s: string) => /[0-9]/.test(s) },
  { label: "1 special character", test: (s: string) => /[^A-Za-z0-9]/.test(s) },
]

type Screen = "login" | "signup" | "terms" | "github-terms"

const makeGithubUrl = (action: "login" | "signup"): string => {
  // Env-aware: the OAuth callback must hit the SAME backend the SPA talks
  // to, using that env's GitHub OAuth app (VITE_* per-environment).
  const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID
  const redirectUri = `${API_URL}/auth/github/login`
  void action
  return `https://github.com/login/oauth/authorize?scope=user:email&client_id=${clientId}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}`
}

/* --------------------------------------------------------------------- */
/* Shared field + button primitives (Lagon)                              */
/* --------------------------------------------------------------------- */

const Label = ({ children }: { children: React.ReactNode }) => (
  <label className="text-[11px] font-mono uppercase tracking-[0.08em] text-[#5F8C84]">
    {children}
  </label>
)

const LagonInput = ({
  focused,
  error,
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  focused: boolean
  error?: boolean
}) => (
  <input
    {...props}
    className={cn(
      "h-11 rounded-[12px] bg-[rgba(4,20,24,0.7)] text-[#D9F2EA] text-[14px] outline-none box-border transition-[border-color,box-shadow] duration-200",
      className,
    )}
    style={{
      border: `1px solid ${error ? "#FF8A7E" : focused ? "#45F0C8" : "#134049"}`,
      boxShadow: focused ? ring : "none",
    }}
  />
)

const PrimaryButton = ({
  enabled,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  enabled: boolean
}) => (
  <button
    {...props}
    disabled={!enabled}
    className="h-[46px] rounded-[12px] border-none text-[14px] font-semibold transition-[box-shadow,background] duration-200"
    style={{
      background: enabled
        ? "linear-gradient(135deg, #45F0C8, #16C2A0)"
        : "rgba(69,240,200,0.1)",
      color: enabled ? "#04141A" : "#4E7A72",
      boxShadow: enabled ? "0 0 24px rgba(69,240,200,0.45)" : "none",
      cursor: enabled ? "pointer" : "not-allowed",
    }}
  >
    {children}
  </button>
)

const GithubButton = ({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    role="button"
    {...props}
    className="flex items-center justify-center gap-[10px] h-11 rounded-[12px] bg-[#161B22] border border-[#2A2F37] text-[#E6EDF3] text-[13px] font-medium cursor-pointer transition-[background,border-color] duration-200 hover:bg-[#1C2128] hover:border-[#3A404A]"
  >
    <FaGithub size={18} />
    {children}
  </div>
)

const ErrorBanner = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-[9px] px-[13px] py-[10px] rounded-[11px] bg-[rgba(255,138,126,0.09)] border border-[rgba(255,138,126,0.38)] text-[#FF8A7E] text-[12.5px] leading-[1.4]">
    <CircleAlert size={15} className="shrink-0" />
    {children}
  </div>
)

const Divider = ({ label, bright }: { label: string; bright?: boolean }) => (
  <div className="flex items-center gap-3">
    <span className="flex-1 h-px bg-border" />
    <span
      className={cn("text-[11px] font-mono whitespace-nowrap", {
        "text-[#5F8C84]": bright,
        "text-[#3E5E59]": !bright,
      })}
    >
      {label}
    </span>
    <span className="flex-1 h-px bg-border" />
  </div>
)

/* --------------------------------------------------------------------- */
/* Brand panel (desktop) + mobile header                                 */
/* --------------------------------------------------------------------- */

const BrandLockup = ({ size = 45 }: { size?: number }) => (
  <div className="flex items-center gap-[10px]">
    <img
      src={ebioseMark}
      alt="Ebiose"
      style={{
        height: size,
        display: "block",
        filter: "drop-shadow(0 0 9px rgba(69,240,200,0.55))",
      }}
    />
    <span
      className="font-semibold text-[#D9F2EA] leading-none"
      style={{
        fontSize: size * 0.6,
        textShadow: "0 0 14px rgba(69,240,200,0.25)",
      }}
    >
      ebiose
      <sup
        className="font-bold text-primary align-super tracking-[0.04em] ml-px"
        style={{
          fontSize: size * 0.3,
          textShadow: "0 0 8px rgba(69,240,200,0.7)",
        }}
      >
        AI
      </sup>
    </span>
  </div>
)

const BrandPanel = () => (
  <div
    className="hidden lg:flex flex-[0_0_46%] max-w-[620px] box-border relative overflow-hidden flex-col justify-between px-14 py-12 border-r border-border"
    style={{
      background:
        "radial-gradient(620px 520px at 30% 6%, #0B3138 0%, rgba(11,49,56,0) 62%), radial-gradient(560px 480px at 92% 112%, #15173B 0%, rgba(21,23,59,0) 58%), #061B20",
    }}
  >
    <BrandLockup />
    <div className="flex flex-col items-center gap-8">
      <svg width="320" height="230" viewBox="0 0 320 230" style={{ overflow: "visible" }}>
        <line x1="56" y1="50" x2="160" y2="112" stroke="#1B4A52" strokeWidth="1.5" />
        <line x1="160" y1="112" x2="272" y2="60" stroke="#1B4A52" strokeWidth="1.5" />
        <line x1="160" y1="112" x2="98" y2="192" stroke="#1B4A52" strokeWidth="1.5" />
        <line x1="160" y1="112" x2="258" y2="180" stroke="#1B4A52" strokeWidth="1.5" />
        <line x1="56" y1="50" x2="98" y2="192" stroke="#13343A" strokeWidth="1.5" />
        <circle cx="56" cy="50" r="8" fill="#0B2A30" stroke="#2E6E72" strokeWidth="2" />
        <circle cx="272" cy="60" r="8" fill="#0B2A30" stroke="#9D7BFF" strokeWidth="2" style={{ filter: "drop-shadow(0 0 8px rgba(157,123,255,0.55))" }} />
        <circle cx="98" cy="192" r="8" fill="#0B2A30" stroke="#2E6E72" strokeWidth="2" />
        <circle cx="258" cy="180" r="8" fill="#0B2A30" stroke="#2E6E72" strokeWidth="2" />
        <circle cx="160" cy="112" r="15" fill="#0B2A30" stroke="#45F0C8" strokeWidth="2.5" style={{ filter: "drop-shadow(0 0 14px rgba(69,240,200,0.85))" }} />
      </svg>
      <div className="flex flex-col gap-3 text-center max-w-[340px]">
        <span className="text-[24px] font-semibold text-foreground leading-[1.2] tracking-[-0.01em]">
          Evolve your agents.
        </span>
        <span className="text-[13px] text-[#7FA8A0] leading-[1.6]">
          Each cycle selects, mutates and recombines your graphs to surface
          better agents.
        </span>
      </div>
    </div>
    <div className="font-mono text-[11px] text-[#3E5E59] tracking-[0.04em]">
      v3 · evolution console
    </div>
  </div>
)

/* --------------------------------------------------------------------- */
/* Screen A — Login                                                      */
/* --------------------------------------------------------------------- */

const LoginScreen = ({
  onSignupEmail,
  onSignupGithub,
}: {
  onSignupEmail: () => void
  onSignupGithub: () => void
}) => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [show, setShow] = useState(false)
  const [focused, setFocused] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get("error") || null,
  )
  const { land, leaveForGithub } = useSignInLanding()

  const enabled = !!(email && password)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await apiClient.post("/auth/login", { email, password })
      land()
    } catch (err) {
      setError(
        (isAxiosError(err) && (err.response?.data as string)) ||
          "Invalid credentials. Check your email and password.",
      )
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="m-auto w-full max-w-[376px] flex flex-col gap-[14px] py-7"
    >
      <div className="flex flex-col gap-[6px]">
        <span className="text-[24px] font-semibold text-foreground tracking-[-0.01em]">
          Login to Ebiose
        </span>
        <span className="text-[13px] text-[#5F8C84]">
          Sign in to your workspace.
        </span>
      </div>

      <div className="flex flex-col gap-[7px]">
        <Label>Email</Label>
        <LagonInput
          type="email"
          placeholder="you@company.com"
          value={email}
          focused={focused === "email"}
          error={!!error}
          onChange={(e) => {
            setEmail(e.target.value)
            setError(null)
          }}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
          className="px-[14px]"
        />
      </div>

      <div className="flex flex-col gap-[7px]">
        <Label>Password</Label>
        <div className="relative">
          <LagonInput
            type={show ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            focused={focused === "pw"}
            error={!!error}
            onChange={(e) => {
              setPassword(e.target.value)
              setError(null)
            }}
            onFocus={() => setFocused("pw")}
            onBlur={() => setFocused(null)}
            className="w-full pl-[14px] pr-11"
          />
          <span
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex cursor-pointer text-[#5F8C84] transition-colors hover:text-primary"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <PrimaryButton type="submit" enabled={enabled}>
        Login
      </PrimaryButton>

      <Divider label="or" />

      <GithubButton onClick={() => leaveForGithub(makeGithubUrl("login"))}>
        Sign in with GitHub
      </GithubButton>

      <Divider label="new to ebiose" bright />

      {/* DEV-496 — email sign-up is temporarily gated off; only GitHub
          sign-up is offered for new accounts. The email entry (and its
          screens) return once DEV-495 ships a verified funnel. */}
      {EMAIL_SIGNUP_ENABLED ? (
        <div className="grid grid-cols-2 gap-[10px]">
          <div
            role="button"
            onClick={onSignupEmail}
            className="flex items-center justify-center gap-2 h-[45px] rounded-[12px] border border-[#1B4A52] bg-[rgba(7,30,35,0.4)] text-[#B8DDD4] text-[12.5px] font-medium cursor-pointer transition-[border-color,box-shadow,color] duration-200 hover:border-primary hover:text-foreground hover:shadow-glow-sm"
          >
            <Mail size={16} className="shrink-0" />
            Sign up with email
          </div>
          <GithubButton onClick={onSignupGithub}>Sign up with GitHub</GithubButton>
        </div>
      ) : (
        <GithubButton onClick={onSignupGithub}>Sign up with GitHub</GithubButton>
      )}
    </form>
  )
}

/* --------------------------------------------------------------------- */
/* Screen C — Sign up                                                    */
/* --------------------------------------------------------------------- */

const SignupScreen = ({
  onBack,
  onValidated,
}: {
  onBack: () => void
  onValidated: (data: {
    firstName: string
    lastName: string
    email: string
    password: string
  }) => void
}) => {
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [show, setShow] = useState(false)
  const [focused, setFocused] = useState<string | null>(null)
  const [error, setError] = useState(false)

  const met = PASSWORD_CRITERIA.map((c) => c.test(password))
  const count = met.filter(Boolean).length
  const pwValid = count === 4
  const strengthColor =
    count >= 4 ? "#68CC58" : count >= 3 ? "#F2C14E" : "#FF8A7E"
  const valid = !!(firstName && lastName && email.includes("@") && pwValid)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) {
      setError(true)
      return
    }
    onValidated({ firstName, lastName, email, password })
  }

  return (
    <form
      onSubmit={submit}
      className="m-auto w-full max-w-[384px] flex flex-col gap-[14px] py-7"
    >
      <div className="flex items-center gap-[11px]">
        <span
          onClick={onBack}
          title="Back"
          className="flex items-center justify-center w-[30px] h-[30px] rounded-[9px] border border-border text-[#5F8C84] cursor-pointer transition-[color,border-color] duration-200 hover:text-primary hover:border-[#1B4A52]"
        >
          <ChevronLeft size={16} strokeWidth={2.2} />
        </span>
        <span className="text-[24px] font-semibold text-foreground tracking-[-0.01em]">
          Sign up to Ebiose
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-[7px] min-w-0">
          <Label>First name</Label>
          <LagonInput
            placeholder="Ada"
            value={firstName}
            focused={focused === "first"}
            error={error}
            onChange={(e) => {
              setFirstName(e.target.value)
              setError(false)
            }}
            onFocus={() => setFocused("first")}
            onBlur={() => setFocused(null)}
            className="w-full px-[13px] rounded-[11px]"
          />
        </div>
        <div className="flex flex-col gap-[7px] min-w-0">
          <Label>Last name</Label>
          <LagonInput
            placeholder="Lovelace"
            value={lastName}
            focused={focused === "last"}
            error={error}
            onChange={(e) => {
              setLastName(e.target.value)
              setError(false)
            }}
            onFocus={() => setFocused("last")}
            onBlur={() => setFocused(null)}
            className="w-full px-[13px] rounded-[11px]"
          />
        </div>
      </div>

      <div className="flex flex-col gap-[7px]">
        <Label>Email</Label>
        <LagonInput
          type="email"
          placeholder="you@company.com"
          value={email}
          focused={focused === "email"}
          error={error}
          onChange={(e) => {
            setEmail(e.target.value)
            setError(false)
          }}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
          className="px-[14px] rounded-[11px]"
        />
      </div>

      <div className="flex flex-col gap-[7px]">
        <Label>Password</Label>
        <div className="relative">
          <LagonInput
            type={show ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            focused={focused === "pw"}
            error={error}
            onChange={(e) => {
              setPassword(e.target.value)
              setError(false)
            }}
            onFocus={() => setFocused("pw")}
            onBlur={() => setFocused(null)}
            className="w-full pl-[14px] pr-11 rounded-[11px]"
          />
          <span
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex cursor-pointer text-[#5F8C84] transition-colors hover:text-primary"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>
      </div>

      {/* strength meter */}
      <div className="flex flex-col gap-[11px] px-[14px] py-[13px] rounded-[12px] bg-[rgba(7,30,35,0.5)] border border-border">
        <div className="flex gap-[5px]">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="flex-1 h-1 rounded-full transition-colors duration-[250ms]"
              style={{
                background: i < count ? strengthColor : "rgba(255,255,255,0.07)",
              }}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-x-[14px] gap-y-[7px]">
          {PASSWORD_CRITERIA.map((c, i) => (
            <div
              key={c.label}
              className="flex items-center gap-[7px] text-[12px] transition-colors duration-200"
              style={{ color: met[i] ? "#68CC58" : "#4E7A72" }}
            >
              <Check size={14} strokeWidth={2.4} className="shrink-0" />
              {c.label}
            </div>
          ))}
        </div>
        {pwValid && (
          <div
            className="flex items-center gap-2 text-[12.5px] font-semibold text-[#68CC58]"
            style={{ textShadow: "0 0 12px rgba(104,204,88,0.45)" }}
          >
            <Check size={16} strokeWidth={2.4} />
            Valid password
          </div>
        )}
      </div>

      {error && (
        <ErrorBanner>
          Fill in all fields and choose a valid password.
        </ErrorBanner>
      )}

      <PrimaryButton type="submit" enabled={valid}>
        Sign Up
      </PrimaryButton>

      <div className="flex items-center justify-center gap-[7px] text-[13px]">
        <span className="text-[#5F8C84]">Already registered?</span>
        <span
          onClick={onBack}
          className="text-primary font-medium cursor-pointer transition-[text-shadow] duration-200 hover:[text-shadow:0_0_12px_rgba(69,240,200,0.6)]"
        >
          Login to your account
        </span>
      </div>
    </form>
  )
}

/* --------------------------------------------------------------------- */
/* Terms of Service (Screen D) — read-gated, interactive                 */
/* --------------------------------------------------------------------- */

// Read-gate: the "I accept" checkbox only unlocks after the user scrolls
// ToSContent to the bottom — acceptance is gated on the full legal text,
// not a summary.
const TermsScreen = ({
  onBack,
  onAccept,
  cta,
}: {
  onBack: () => void
  onAccept: () => void | Promise<void>
  cta: string
}) => {
  const [open, setOpen] = useState(true)
  const [read, setRead] = useState(false)
  const [progress, setProgress] = useState(0)
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // A document that fits without scrolling is "read" on sight — never
  // strand the user behind a gate they cannot satisfy.
  useEffect(() => {
    const el = scrollRef.current
    if (open && el && el.scrollHeight - el.clientHeight <= 4) {
      setRead(true)
      setProgress(100)
    }
  }, [open])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    const max = el.scrollHeight - el.clientHeight
    const pct = max <= 0 ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100))
    setProgress(pct)
    if (max <= 0 || el.scrollTop >= max - 4) setRead(true)
  }

  const submit = async () => {
    if (!accepted || submitting) return
    setSubmitting(true)
    try {
      await Promise.resolve(onAccept())
    } finally {
      setSubmitting(false)
    }
  }

  const progColor = read ? "#68CC58" : "#45F0C8"

  return (
    <div className="m-auto flex w-full max-w-[440px] flex-col gap-[14px] py-7">
      {/* header: back + title */}
      <div className="flex items-center gap-[11px]">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] border border-border text-[#5F8C84] transition-colors hover:border-border-strong hover:text-primary"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex flex-col gap-[3px]">
          <span className="text-[24px] font-semibold tracking-[-0.01em] text-foreground">
            Terms of Service
          </span>
          <span className="text-[12.5px] text-[#5F8C84]">
            Read and accept before creating your account.
          </span>
        </div>
      </div>

      {/* accordion */}
      <div className="overflow-hidden rounded-[12px] border border-border bg-[rgba(7,30,35,0.5)]">
        <div
          onClick={() => setOpen((o) => !o)}
          className="flex cursor-pointer items-center justify-between gap-2.5 px-4 py-3.5"
        >
          <span className="text-[13px] font-medium text-[#D9F2EA]">
            General Terms of Service
          </span>
          <span className="flex items-center gap-2.5">
            {open && (
              <span
                className="font-mono text-[11px]"
                style={{ color: progColor }}
              >
                {progress}% read
              </span>
            )}
            <ChevronDown
              size={17}
              className="shrink-0 text-[#5F8C84] transition-transform duration-200"
              style={{ transform: open ? "rotate(180deg)" : "none" }}
            />
          </span>
        </div>

        {open && (
          <div className="relative border-t border-border">
            {/* read-progress bar */}
            <div className="h-[3px] bg-[rgba(255,255,255,0.06)]">
              <div
                className="h-full transition-[width] duration-150"
                style={{
                  width: `${progress}%`,
                  background: progColor,
                  boxShadow: read
                    ? "0 0 10px rgba(104,204,88,0.6)"
                    : "0 0 10px rgba(69,240,200,0.5)",
                }}
              />
            </div>

            {/* scrollable legal body — the real ToS document */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="max-h-[210px] select-text overflow-y-auto px-4 pb-5 pt-2 text-[12.5px] leading-[1.65] text-[#8FC4B8] [&_h1]:text-[14px] [&_h1]:text-[#D9F2EA] [&_h2]:text-[12.5px] [&_h2]:text-[#A9CFC6] [&_strong]:text-[#D9F2EA]"
            >
              <ToSContent />
            </div>

            {/* scroll-to-bottom hint, until read */}
            {!read && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex h-[58px] items-end justify-center bg-[linear-gradient(rgba(7,20,24,0),rgba(7,22,26,0.97))] pb-2.5">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-[rgba(4,20,24,0.92)] px-3 py-1 font-mono text-[11px] text-[#7FA8A0]">
                  <ArrowDown size={12} />
                  Scroll to the bottom
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* accept checkbox — locked until read */}
      <div
        onClick={() => read && setAccepted((a) => !a)}
        title={read ? "" : "Read the terms to the bottom first"}
        className="flex items-center gap-[11px] rounded-[11px] border px-3.5 py-3 transition-colors"
        style={{
          borderColor: accepted
            ? "rgba(69,240,200,0.4)"
            : read
              ? "#1B4A52"
              : "#11393F",
          background: accepted ? "rgba(69,240,200,0.06)" : "transparent",
          cursor: read ? "pointer" : "not-allowed",
        }}
      >
        <span
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-[6px] border-[1.5px] transition-all"
          style={{
            borderColor: accepted
              ? "#45F0C8"
              : read
                ? "#2E6E72"
                : "#1B4A52",
            background: accepted ? "#45F0C8" : "transparent",
          }}
        >
          {accepted ? (
            <Check size={13} strokeWidth={3} className="text-[#04141A]" />
          ) : !read ? (
            <Lock size={11} strokeWidth={2.2} className="text-[#4E7A72]" />
          ) : null}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className="text-[13px] transition-colors"
            style={{ color: read ? "#D9F2EA" : "#5F8C84" }}
          >
            I accept the Terms of Service
          </span>
          {read ? (
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-[#68CC58]">
              <Check size={11} strokeWidth={2.6} />
              Document read — you can accept
            </span>
          ) : (
            <span className="font-mono text-[11px] text-[#5F8C84]">
              Read the terms to the end to enable
            </span>
          )}
        </div>
      </div>

      <PrimaryButton enabled={accepted && !submitting} onClick={submit}>
        {submitting ? "Please wait…" : cta}
      </PrimaryButton>
    </div>
  )
}

/* --------------------------------------------------------------------- */
/* Root                                                                  */
/* --------------------------------------------------------------------- */

export default function LoginView() {
  const { land, leaveForGithub } = useSignInLanding()
  const [screen, setScreen] = useState<Screen>("login")
  const [pending, setPending] = useState<{
    firstName: string
    lastName: string
    email: string
    password: string
  } | null>(null)

  const handleTermsAccepted = async (accepted: boolean) => {
    if (!accepted || !pending) return
    try {
      await apiClient.post("/auth/signup", { ...pending, githubId: "" })
      await apiClient.post("/auth/login", {
        email: pending.email,
        password: pending.password,
      })
      land()
    } catch (err) {
      console.error("Signup failed:", err)
      setScreen("signup")
    }
  }

  const handleGithubTerms = (accepted: boolean) => {
    if (!accepted) return
    leaveForGithub(makeGithubUrl("signup"))
  }

  return (
    <div className="flex min-h-screen w-full bg-background font-sans">
      <BrandPanel />
      <div className="flex-1 min-w-0 relative bg-background flex flex-col overflow-y-auto px-6 py-8 lg:px-12">
        {/* mobile brand header (brand panel is desktop-only) */}
        <div className="lg:hidden pb-[6px]">
          <BrandLockup size={39} />
        </div>
        {screen === "login" && (
          <LoginScreen
            onSignupEmail={() => setScreen("signup")}
            onSignupGithub={() => setScreen("github-terms")}
          />
        )}
        {screen === "signup" && (
          <SignupScreen
            onBack={() => setScreen("login")}
            onValidated={(data) => {
              setPending(data)
              setScreen("terms")
            }}
          />
        )}
        {screen === "terms" && (
          <TermsScreen
            onBack={() => setScreen("signup")}
            onAccept={() => handleTermsAccepted(true)}
            cta="Sign Up"
          />
        )}
        {screen === "github-terms" && (
          <TermsScreen
            onBack={() => setScreen("login")}
            onAccept={() => handleGithubTerms(true)}
            cta="Continue with GitHub"
          />
        )}
      </div>
    </div>
  )
}
